import { useState } from 'react';
import { ChatText, PencilSimple, Plus, Trash, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  COMMENT_KINDS,
  COMMENT_KIND_IDS,
  COMMENT_MAX_LENGTH,
  authorLabel,
  fmtCommentDate,
  isPositiveKind,
  type ConversationComment,
  type ConversationCommentKind,
} from '../../lib/admin-conversation-comments';

/* Surface de revue passée par la page. Optionnelle de bout en bout : la preview
   de conception et tout rendu hors session la laissent absente, et l'écran de
   lecture reste alors strictement identique à ce qu'il était. */
export interface ConversationReviewSurface {
  readonly comments: readonly ConversationComment[];
  readonly busy: boolean;
  readonly error: string | null;
  /* Identifiant du relecteur courant : seul l'auteur corrige ou retire. */
  readonly currentUserId: string | null;
  /* Rendent `false` quand l'écriture a échoué. Le formulaire reste alors
     ouvert avec son texte : refermer sur un refus ferait perdre à Ismael ce
     qu'il vient d'écrire, au moment précis où il faut le relire. */
  add(input: { messageId: string | null; kind: ConversationCommentKind; body: string }): Promise<boolean>;
  edit(id: string, patch: { kind: ConversationCommentKind; body: string }): Promise<boolean>;
  remove(id: string): Promise<boolean>;
}

const DEFAULT_KIND: ConversationCommentKind = 'wrong_answer';

function KindBadge({ kind }: { kind: ConversationCommentKind }) {
  const meta = COMMENT_KINDS[kind];
  const tone = isPositiveKind(kind)
    ? 'bg-[var(--naia-green-bg)] text-[var(--naia-green-deep)]'
    : 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]';
  return (
    <span
      title={meta.hint}
      className={`inline-block shrink-0 rounded-[4px] px-[6px] py-[2px] text-[10px] font-extrabold uppercase tracking-[0.5px] ${tone}`}
    >
      {meta.label}
    </span>
  );
}

function KindPicker({
  value,
  onChange,
  id,
}: {
  value: ConversationCommentKind;
  onChange: (kind: ConversationCommentKind) => void;
  id: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-bold text-[var(--naia-text-secondary)]">
        Nature
      </label>
      <NativeSelect
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as ConversationCommentKind)}
        className="min-h-[44px] sm:min-h-[36px]"
      >
        {COMMENT_KIND_IDS.map((kind) => (
          <option key={kind} value={kind}>
            {COMMENT_KINDS[kind].label}
          </option>
        ))}
      </NativeSelect>
      <p className="text-[11.5px] leading-[1.45] text-[var(--naia-text-secondary)]">{COMMENT_KINDS[value].hint}</p>
    </div>
  );
}

function CommentForm({
  idPrefix,
  initialKind = DEFAULT_KIND,
  initialBody = '',
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  initialKind?: ConversationCommentKind;
  initialBody?: string;
  submitLabel: string;
  busy: boolean;
  onSubmit: (kind: ConversationCommentKind, body: string) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<ConversationCommentKind>(initialKind);
  const [body, setBody] = useState(initialBody);
  const trimmed = body.trim();
  const tooLong = trimmed.length > COMMENT_MAX_LENGTH;

  return (
    <form
      className="mt-2 flex flex-col gap-2 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card-alt)] p-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed === '' || tooLong || busy) return;
        onSubmit(kind, trimmed);
      }}
    >
      <KindPicker id={`${idPrefix}-kind`} value={kind} onChange={setKind} />

      <div className="flex flex-col gap-1">
        <label htmlFor={`${idPrefix}-body`} className="text-[11px] font-bold text-[var(--naia-text-secondary)]">
          Commentaire
        </label>
        <Textarea
          id={`${idPrefix}-body`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Ce que vous avez constaté, en une ou deux phrases."
          className="text-[13px] text-[var(--naia-text-primary)]"
        />
        <span
          className={`self-end text-[11px] tabular-nums ${
            tooLong ? 'text-[var(--naia-error)]' : 'text-[var(--naia-text-secondary)]'
          }`}
        >
          {trimmed.length} / {COMMENT_MAX_LENGTH}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={trimmed === '' || tooLong || busy} className="min-h-[44px] sm:min-h-[32px]">
          {submitLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCancel}
          className="min-h-[44px] sm:min-h-[32px]"
        >
          Annuler
        </Button>
      </div>
    </form>
  );
}

function CommentRow({ comment, review }: { comment: ConversationComment; review: ConversationReviewSurface }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /* Seul l'auteur corrige ou retire : un commentaire porte un nom, le laisser
     réécrire par un tiers ferait dire à quelqu'un ce qu'il n'a pas dit. Le
     serveur le refuse de toute façon — ceci évite d'offrir un geste voué à
     échouer, ce n'est pas le contrôle. */
  const mine = review.currentUserId !== null && comment.authorId === review.currentUserId;

  if (editing) {
    return (
      <CommentForm
        idPrefix={`edit-${comment.id}`}
        initialKind={comment.kind}
        initialBody={comment.body}
        submitLabel="Enregistrer"
        busy={review.busy}
        onCancel={() => setEditing(false)}
        onSubmit={(kind, body) => {
          void review.edit(comment.id, { kind, body }).then((ok) => ok && setEditing(false));
        }}
      />
    );
  }

  return (
    <li className="rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card-alt)] p-2.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <KindBadge kind={comment.kind} />
        <span className="text-[11.5px] font-semibold text-[var(--naia-text-primary)]">{authorLabel(comment)}</span>
        <span className="text-[11.5px] tabular-nums text-[var(--naia-text-secondary)]">
          {fmtCommentDate(comment.createdAt)}
        </span>
        {comment.editedAt && (
          <span
            className="text-[11px] italic text-[var(--naia-text-secondary)]"
            title={`Corrigé le ${fmtCommentDate(comment.editedAt)}`}
          >
            corrigé
          </span>
        )}
        {mine && (
          <span className="ml-auto flex flex-wrap gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-[44px] sm:min-h-[28px] px-2"
              onClick={() => setEditing(true)}
            >
              <PencilSimple size={13} />
              Corriger
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="min-h-[44px] sm:min-h-[28px] px-2"
              onClick={() => setConfirming((c) => !c)}
            >
              {confirming ? <X size={13} /> : <Trash size={13} />}
              {confirming ? 'Garder' : 'Retirer'}
            </Button>
          </span>
        )}
      </div>

      <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.55] text-[var(--naia-text-primary)]">
        {comment.body}
      </p>

      {confirming && (
        <div className="mt-2 flex flex-col gap-2 rounded-[var(--naia-r-sm)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-2">
          {/* Le retrait est doux : la ligne reste en base, l'écran cesse de la
              montrer. Le dire ici évite qu'on le croie irréversible — ou, pire,
              qu'on le croie sans trace. */}
          <p className="text-[12px] text-[var(--naia-text-secondary)]">
            Le commentaire sort de cet écran. La ligne reste en base avec son auteur et sa date : un commentaire
            d'audit ne disparaît pas sans trace.
          </p>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={review.busy}
            className="min-h-[44px] sm:min-h-[32px] self-start"
            onClick={() => {
              void review.remove(comment.id).then((ok) => ok && setConfirming(false));
            }}
          >
            Retirer le commentaire
          </Button>
        </div>
      )}
    </li>
  );
}

/*
 * Un bloc de commentaires ancré. `messageId === null` = le fil entier.
 *
 * L'ancrage sur un TOUR est le cas qui vaut : « la réponse a inventé une
 * source » n'a de valeur pour l'amélioration que si l'on sait laquelle. Le
 * commentaire de fil reste offert pour ce qui ne tient à aucun tour, et il est
 * volontairement moins accessible — il est en bas de page, pas sur chaque tour.
 */
export function CommentAnchor({
  messageId,
  review,
  emptyLabel,
  addLabel,
}: {
  messageId: string | null;
  review: ConversationReviewSurface;
  emptyLabel?: string;
  addLabel: string;
}) {
  const [composing, setComposing] = useState(false);
  const mine = review.comments.filter((c) => c.messageId === messageId);

  return (
    <div className="mt-2 border-t border-[var(--naia-line)] pt-2">
      {mine.length > 0 && (
        <ul className="mb-2 flex flex-col gap-2">
          {mine.map((comment) => (
            <CommentRow key={comment.id} comment={comment} review={review} />
          ))}
        </ul>
      )}

      {mine.length === 0 && emptyLabel && (
        <p className="mb-2 text-[12px] text-[var(--naia-text-secondary)]">{emptyLabel}</p>
      )}

      {composing ? (
        <CommentForm
          idPrefix={`new-${messageId ?? 'thread'}`}
          submitLabel="Commenter"
          busy={review.busy}
          onCancel={() => setComposing(false)}
          onSubmit={(kind, body) => {
            void review.add({ messageId, kind, body }).then((ok) => ok && setComposing(false));
          }}
        />
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-[44px] sm:min-h-[28px]"
          onClick={() => setComposing(true)}
        >
          {messageId === null ? <ChatText size={13} /> : <Plus size={13} />}
          {addLabel}
        </Button>
      )}

      {review.error && <p className="mt-2 text-[12px] text-[var(--naia-error)]">{review.error}</p>}
    </div>
  );
}
